import { jwtDecode } from 'jwt-decode'
import type { AuthUser, JwtPayload, Role } from './types'

const LS_KEY = 'lv_admin_token'
const SS_KEY = 'lv_admin_token_ss'
const LEGACY_LS = 'legevakt_token'
const LEGACY_SS = 'legevakt_session_token'

function normalizeList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string') return value.split(/\s+/)
  return []
}

function normalizeRole(payload: JwtPayload & Record<string, unknown>): Role {
  const roles = [
    ...normalizeList(payload.roles),
    ...normalizeList(payload.scope),
    ...normalizeList(payload.scopes),
    typeof payload.role === 'string' ? payload.role : '',
  ].filter(Boolean)

  if (payload.is_admin || roles.includes('admin')) return 'admin'
  if (payload.is_superuser || roles.includes('superuser') || roles.includes('super') || roles.includes('drift')) {
    return 'superuser'
  }
  if (roles.includes('staff')) return 'staff'
  return 'user'
}

function normalizePermissions(payload: JwtPayload & Record<string, unknown>): string[] {
  const values = [
    ...normalizeList(payload.permissions),
    ...normalizeList(payload.perms),
    ...normalizeList(payload.scope),
    ...normalizeList(payload.scopes),
  ]
  return Array.from(new Set(values.map((value) => value.trim().toLowerCase()).filter(Boolean)))
}

function parseToken(token: string): AuthUser | null {
  try {
    const payload = jwtDecode<JwtPayload & Record<string, unknown>>(token)
    if (payload.exp && Date.now() >= payload.exp * 1000) return null

    const rawCid = payload.customer_id ?? payload.customerId ?? payload.customer ?? payload.cid
    const customer_id = typeof rawCid === 'string' ? Number(rawCid) : rawCid

    return {
      username: payload.sub || payload.username,
      email: payload.email,
      role: normalizeRole(payload),
      permissions: normalizePermissions(payload),
      customer_permissions: normalizeList(payload.customer_permissions)
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean),
      customer_role:
        typeof payload.customer_role === 'string'
          ? (payload.customer_role as Role)
          : undefined,
      auth_context:
        typeof payload.auth_context === 'string' ? payload.auth_context : undefined,
      customer_id: Number.isFinite(customer_id) ? Number(customer_id) : undefined,
      token,
    }
  } catch {
    return null
  }
}

export function getStoredUser(): AuthUser | null {
  const token = window.localStorage.getItem(LS_KEY) || window.sessionStorage.getItem(SS_KEY)
  if (!token) return null
  return parseToken(token)
}

export function storeToken(token: string, remember: boolean) {
  clearToken()
  if (remember) {
    window.localStorage.setItem(LS_KEY, token)
  } else {
    window.sessionStorage.setItem(SS_KEY, token)
  }
}

export function replaceStoredToken(token: string) {
  const remember = window.localStorage.getItem(LS_KEY) !== null
  storeToken(token, remember)
}

export function clearToken() {
  window.localStorage.removeItem(LS_KEY)
  window.sessionStorage.removeItem(SS_KEY)
  window.localStorage.removeItem(LEGACY_LS)
  window.sessionStorage.removeItem(LEGACY_SS)
}
